import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Dialog,
  TextField,
  Button,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";
import { format } from "date-fns";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp,
  TrendingDown,
  Launch as LaunchIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import TF from "../api/requests/tf_backend";

const AlertsTable = () => {
  const params = new URLSearchParams(location.search);
  const symbol = params.get("symbol");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [mainOrder, setMainOrder] = useState({
    quantity: "",
    amount: "",
  });

  const [gttOrders, setGttOrders] = useState([
    { price: selectedAlert?.stockDetails?.ltp || 0, amount: "", quantity: "" },
  ]);

  useEffect(() => {
    const getAlerts = async () => {
      try {
        const response = await TF.fetchAlerts();
        setAlerts(response.alerts);
      } catch (error) {
        console.error("Error fetching alerts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (symbol) {
      handleRowClick({ trading_symbol: symbol });
    }
    getAlerts();
  }, []);

  const fetchStockDetails = async (symbol) => {
    try {
      const stockData = await TF.fetchStockDetails({ symbol, duration: 30 });
      return {
        ...stockData?.quoteInfo?.[`NSE:${symbol}`],
        percentageChange: stockData.percentageChange,
        yesterdayClose: stockData.yesterdayClose,
        ltp: stockData.ltp,
      };
    } catch (error) {
      console.error("Error fetching stock details:", error);
      return null;
    }
  };

  const handleRowClick = async (alert) => {
    setDialogLoading(true);
    setSelectedAlert(alert);
    let stockDetails = null;
    try {
      stockDetails = await fetchStockDetails(alert.trading_symbol);
      setSelectedAlert((prev) => ({ ...prev, stockDetails }));
    } catch (error) {
      console.error("Error fetching stock details:", error);
    } finally {
      setDialogLoading(false);
    }
    setMainOrder({ quantity: "", amount: "" });
    setGttOrders([
      { price: stockDetails?.last_price || 0, amount: "", quantity: "" },
    ]);
  };

  const handleMainOrderChange = (field, value) => {
    // const numValue = value.replace(/[^0-9]/g, "");
    const numValue = value === "" ? "" : parseFloat(value);
    const triggerPrice =
      selectedAlert?.stockDetails?.ltp || selectedAlert?.trigger_price || 0;

    setMainOrder((prev) => {
      const newDetails = { ...prev };
      if (field === "quantity") {
        newDetails.quantity = numValue;
        newDetails.amount = numValue * triggerPrice;
      } else {
        newDetails.amount = numValue;
        newDetails.quantity = Math.floor(numValue / triggerPrice);
      }
      return newDetails;
    });
  };

  const handleGttChange = (index, field, value) => {
    const numValue = value === "" ? "" : value;

    setGttOrders((prev) => {
      const newOrders = [...prev];
      const order = { ...newOrders[index] };

      if (field === "quantity") {
        order.quantity = numValue;
        order.amount =
          numValue && order.price
            ? (parseFloat(numValue) * parseFloat(order.price)).toFixed(2)
            : "";
      } else if (field === "amount") {
        order.amount = numValue;
        order.quantity =
          numValue && order.price
            ? Math.floor(parseFloat(numValue) / parseFloat(order.price))
            : "";
      } else {
        order.price = numValue;
        if (order.quantity && numValue) {
          order.amount = (
            parseFloat(order.quantity) * parseFloat(numValue)
          ).toFixed(2);
        }
      }

      newOrders[index] = order;
      return newOrders;
    });
  };

  const handlePlaceOrder = async () => {
    try {
      await TF.placeOrder({
        tradingsymbol: selectedAlert.trading_symbol,
        exchange: "NSE",
        transaction_type: "BUY",
        quantity: parseInt(mainOrder.quantity),
        order_type: "MARKET",
        product: "CNC",
        variety: "regular",
      });

      setOrderSuccess({
        type: "success",
        message: "Order placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place order.",
      });
    }
  };

  const handlePlaceGttOrders = async () => {
    try {
      const promises = gttOrders.map((order) =>
        TF.placeOrder({
          tradingsymbol: selectedAlert.trading_symbol,
          exchange: "NSE",
          transaction_type: "BUY",
          quantity: parseInt(order.quantity),
          order_type: "LIMIT",
          price: parseFloat(order.price),
          product: "CNC",
          variety: "regular",
        })
      );

      await Promise.all(promises);
      setOrderSuccess({
        type: "success",
        message: "LIMIT orders placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place LIMIT orders.",
      });
    }
  };

  const handleScreenerRedirect = (e, symbol) => {
    e.stopPropagation();
    window.open(`https://www.screener.in/company/${symbol}`, "_blank");
  };

  const handleTradingViewRedirect = (e, symbol) => {
    e.stopPropagation();
    window.open(
      `https://www.tradingview.com/chart?symbol=NSE:${symbol}`,
      "_blank"
    );
  };

  if (loading) {
    return <Typography>Loading alerts...</Typography>;
  }

  return (
    <Box sx={{ width: "100%", px: 2, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "#1a2027",
              letterSpacing: "-0.01em",
            }}
          >
            Trading Alerts
          </Typography>
          <Chip
            label={`${alerts.length} alerts`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.75rem",
              bgcolor: "#f0f4f8",
              color: "#5c6bc0",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Last updated: {format(new Date(), "HH:mm:ss")}
          </Typography>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed", // Fixed table layout
            width: "100%", // Full width
            minWidth: "auto", // Remove minimum width
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                  padding: "8px 16px",
                  borderBottom: "1px solid #e0e0e0",
                  width: "15%", // Symbol column
                }}
              >
                Symbol
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                  padding: "8px 16px",
                  width: "15%", // Price column
                }}
              >
                Price
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                  padding: "8px 16px",
                  width: "25%", // Scan column
                }}
              >
                Scan
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                  padding: "8px 16px",
                  width: "12%", // Time column
                }}
              >
                Time
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                  padding: "8px 16px",
                  width: "8%", // Links column
                }}
              >
                Links
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((alert, index) => (
              <TableRow
                key={index}
                onClick={() => handleRowClick(alert)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f5f5f5" },
                  "&:nth-of-type(odd)": { bgcolor: "#fafafa" },
                  "& td": {
                    fontSize: "12px",
                    padding: "6px 16px",
                    borderBottom: "1px solid #f0f0f0",
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 500,
                    color: "#1a73e8",
                    width: "15%",
                  }}
                >
                  {alert.trading_symbol}
                </TableCell>
                <TableCell
                  sx={{
                    color: alert.trigger_price > 0 ? "#137333" : "#444",
                    width: "15%",
                  }}
                >
                  ₹{alert.trigger_price}
                </TableCell>
                <TableCell
                  sx={{
                    color: "#444",
                    width: "25%",
                  }}
                >
                  {alert.scan_name}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    color: "#666",
                    width: "12%",
                  }}
                >
                  {format(new Date(alert.triggered_at), "HH:mm:ss")}
                </TableCell>
                <TableCell sx={{ width: "8%" }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        handleScreenerRedirect(e, alert.trading_symbol)
                      }
                      sx={{
                        padding: "3px",
                        color: "#1976d2",
                        "& svg": { fontSize: "16px" },
                      }}
                    >
                      <LaunchIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        handleTradingViewRedirect(e, alert.trading_symbol)
                      }
                      sx={{
                        padding: "3px",
                        color: "#1976d2",
                        "& svg": { fontSize: "16px" },
                      }}
                    >
                      <ShowChartIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(selectedAlert)}
        onClose={() => setSelectedAlert(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAlert && (
          <Box sx={{ p: 3 }}>
            {dialogLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {orderSuccess && (
                  <Alert
                    severity={orderSuccess.type}
                    sx={{ mb: 2 }}
                    onClose={() => setOrderSuccess(null)}
                  >
                    {orderSuccess.message}
                  </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 500 }}>
                      {selectedAlert.trading_symbol}
                    </Typography>
                    <Chip label="NSE" size="small" sx={{ height: 24 }} />
                    <Typography sx={{ fontSize: "0.875rem" }}>
                      TP: ₹{selectedAlert.trigger_price}
                    </Typography>

                    <Typography sx={{ fontSize: "0.875rem" }}>
                      LTP: ₹{selectedAlert.stockDetails?.ltp}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          handleScreenerRedirect(
                            e,
                            selectedAlert.trading_symbol
                          )
                        }
                        sx={{
                          padding: "3px",
                          color: "#1976d2",
                          "& svg": { fontSize: "16px" },
                        }}
                      >
                        <LaunchIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          handleTradingViewRedirect(
                            e,
                            selectedAlert.trading_symbol
                          )
                        }
                        sx={{
                          padding: "3px",
                          color: "#1976d2",
                          "& svg": { fontSize: "16px" },
                        }}
                      >
                        <ShowChartIcon />
                      </IconButton>
                    </Box>

                    {selectedAlert.stockDetails && (
                      <Chip
                        icon={
                          selectedAlert.stockDetails.percentageChange >= 0 ? (
                            <TrendingUp />
                          ) : (
                            <TrendingDown />
                          )
                        }
                        label={`${selectedAlert.stockDetails.percentageChange?.toFixed(
                          2
                        )}%`}
                        color={
                          selectedAlert.stockDetails.percentageChange >= 0
                            ? "success"
                            : "error"
                        }
                        size="small"
                        sx={{ height: 24 }}
                      />
                    )}
                  </Box>

                  {selectedAlert.stockDetails && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: "0.875rem", color: "#666" }}
                        >
                          Previous Close
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem" }}>
                          ₹{selectedAlert.stockDetails.yesterdayClose}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: "0.875rem", color: "#666" }}
                        >
                          Day's Range
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem" }}>
                          ₹{selectedAlert.stockDetails.ohlc?.low} - ₹
                          {selectedAlert.stockDetails.ohlc?.high}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: "0.875rem", color: "#666" }}
                        >
                          Circuit Limits
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem" }}>
                          ₹{selectedAlert.stockDetails.lower_circuit_limit} - ₹
                          {selectedAlert.stockDetails.upper_circuit_limit}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Box>

                {/* Market Order */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{ mb: 1, color: "#666", fontSize: "0.875rem" }}
                  >
                    Market Order
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Amount"
                      value={
                        mainOrder.amount || selectedAlert?.stockDetails?.ltp
                      }
                      onChange={(e) =>
                        handleMainOrderChange("amount", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                    />
                    <TextField
                      size="small"
                      placeholder="Quantity"
                      value={mainOrder.quantity}
                      onChange={(e) =>
                        handleMainOrderChange("quantity", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handlePlaceOrder}
                    sx={{ mt: 1, fontSize: "0.875rem" }}
                  >
                    Place Market Order
                  </Button>
                </Box>

                {/* LIMIT Orders */}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ color: "#666", fontSize: "0.875rem" }}>
                      LIMIT Orders
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setGttOrders((prev) => [
                          ...prev,
                          { price: selectedAlert?.stockDetails?.ltp, amount: "", quantity: "" },
                        ])
                      }
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {console.log(gttOrders)}
                  {gttOrders.map((order, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Price"
                          value={
                            order.price || selectedAlert?.stockDetails?.ltp
                          }
                          onChange={(e) =>
                            handleGttChange(index, "price", e.target.value)
                          }
                          sx={{ flex: 1 }}
                          InputProps={{ sx: { fontSize: "0.875rem" } }}
                        />
                        <TextField
                          size="small"
                          placeholder="Quantity"
                          value={order.quantity}
                          onChange={(e) =>
                            handleGttChange(index, "quantity", e.target.value)
                          }
                          sx={{ flex: 1 }}
                          InputProps={{ sx: { fontSize: "0.875rem" } }}
                        />
                        <TextField
                          size="small"
                          placeholder="Amount"
                          value={order.amount}
                          onChange={(e) =>
                            handleGttChange(index, "amount", e.target.value)
                          }
                          sx={{ flex: 1 }}
                          InputProps={{ sx: { fontSize: "0.875rem" } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            setGttOrders((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}

                  {gttOrders.length > 0 && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handlePlaceGttOrders}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Place LIMIT Orders
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AlertsTable;
